import type {
  Query,
  WhereFilterOp,
  OrderByDirection,
} from "firebase/firestore";
import type { Snapshot } from "@/hooks/document-subscriptions";
import { queryClient } from "@/contexts/react-query/query-client";
import { readSessionUser, writeSessionUser } from "./user-session-cache";

export type QuerySpec = {
  collection: string;
  filters: Array<[string, WhereFilterOp, unknown]>;
  order: [string, OrderByDirection];
  limit: number;
};
export type QuerySource = Query | QuerySpec;
type Publish = (snapshot: Snapshot) => void;
const initialReads = new Set<Promise<unknown>>();

/** Let initial data render before loading the persistent, real-time client. */
async function liveRuntime() {
  const deadline = Date.now() + 1500;
  do {
    if (initialReads.size && Date.now() < deadline) {
      let timer: ReturnType<typeof setTimeout>;
      await Promise.race([
        Promise.allSettled(initialReads),
        new Promise((resolve) => {
          timer = setTimeout(resolve, Math.max(0, deadline - Date.now()));
        }),
      ]);
      clearTimeout(timer!);
    }
    if (queryClient.isFetching() && Date.now() < deadline) {
      await new Promise<void>((resolve) => {
        let unsubscribe = () => {};
        const timer = setTimeout(done, Math.max(0, deadline - Date.now()));
        unsubscribe = queryClient.getQueryCache().subscribe(() => {
          if (!queryClient.isFetching()) {
            done();
          }
        });
        function done() {
          clearTimeout(timer);
          unsubscribe();
          resolve();
        }
      });
    }
    // A profile response can mount another foreground request for its photo.
    // Wait for that render and recheck rather than competing with the photo.
    if (
      typeof requestAnimationFrame !== "undefined" &&
      document.visibilityState === "visible"
    ) {
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, 100);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            clearTimeout(timer);
            resolve();
          }),
        );
      });
    }
  } while (
    (initialReads.size || queryClient.isFetching()) &&
    Date.now() < deadline
  );
  return Promise.all([import("firebase/firestore"), import("./firebase.init")]);
}

export function subscribeToDocument(
  path: string,
  publish: Publish,
  uid?: string,
  initialValue?: unknown,
) {
  let active = true;
  let stop: (() => void) | undefined;
  let liveStarted = false;
  let livePublished = false;
  let initialPublished = false;
  const failure = (error: unknown) => {
    if (active) {
      writeSessionUser(uid, path, undefined);
      publish({ value: undefined, status: "error", error });
    }
  };
  const startLive = async () => {
    if (!active || liveStarted) {
      return;
    }
    liveStarted = true;
    try {
      const [sdk, initialized] = await liveRuntime();
      if (!active) {
        return;
      }
      stop = sdk.onSnapshot(
        sdk.doc(initialized.firestore, path),
        (snapshot) => {
          if (!active || (initialPublished && snapshot.metadata.fromCache)) {
            return;
          }
          livePublished = true;
          if (!snapshot.metadata.fromCache) {
            writeSessionUser(uid, path, snapshot.data());
          }
          publish({ value: snapshot.data(), status: "success", error: null });
        },
        failure,
      );
    } catch (error) {
      failure(error);
    }
  };
  const cached = initialValue ?? readSessionUser(uid, path);
  if (cached) {
    initialPublished = true;
    publish({ value: cached, status: "success", error: null });
    void startLive();
  } else if (typeof navigator !== "undefined" && !navigator.onLine) {
    void startLive();
  } else {
    const read = import("./firebase.read").then(
      ({ readFirestore, doc, getDoc }) =>
        active ? getDoc(doc(readFirestore, path)) : undefined,
    );
    initialReads.add(read);
    void read
      .then((snapshot) => {
        if (active && snapshot && !livePublished) {
          initialPublished = true;
          writeSessionUser(uid, path, snapshot.data());
          publish({ value: snapshot.data(), status: "success", error: null });
        }
      })
      .catch(() => {})
      .finally(() => {
        initialReads.delete(read);
        void startLive();
      });
  }
  // A network-only read must not delay offline-cache fallback indefinitely.
  const timer = setTimeout(() => {
    void startLive();
  }, 1500);
  return () => {
    active = false;
    clearTimeout(timer);
    stop?.();
  };
}

export function subscribeToQuery(source: QuerySource, publish: Publish) {
  let active = true;
  let stop: (() => void) | undefined;
  void liveRuntime()
    .then(([sdk, initialized]) => {
      if (!active) {
        return;
      }
      const query =
        "collection" in source
          ? sdk.query(
              sdk.collection(initialized.firestore, source.collection),
              ...source.filters.map(([field, operator, value]) =>
                sdk.where(field, operator, value),
              ),
              sdk.orderBy(...source.order),
              sdk.limit(source.limit),
            )
          : source;
      stop = sdk.onSnapshot(
        query,
        (snapshot) => {
          if (active) {
            publish({
              value: snapshot.docs.map((item) => ({
                ...item.data(),
                id: item.id,
              })),
              status: "success",
              error: null,
            });
          }
        },
        (error) => {
          if (active) {
            publish({ value: undefined, status: "error", error });
          }
        },
      );
    })
    .catch((error) => {
      if (active) {
        publish({ value: undefined, status: "error", error });
      }
    });
  return () => {
    active = false;
    stop?.();
  };
}
